// frontend/src/super-admin/support/SupportInboxPage.tsx
// Super-admin: inbox list of all support tickets with status filter.

import * as React from 'react';
import { Link } from 'react-router-dom';
import { LifeBuoy } from 'lucide-react';
import { PageHeader } from '../../design-system/components/PageHeader';
import { LoadingState } from '../../design-system/components/LoadingState';
import { ErrorState } from '../../design-system/components/ErrorState';
import { VISUAL, statusPill, type StatusTone } from '../../design-system/visual';
import { adminSupportApi, type SupportTicket, type TicketStatus } from '../../modules/support/api';

const STATUSES: Array<{ value: TicketStatus | 'all'; label: string }> = [
  { value: 'all',     label: 'All' },
  { value: 'open',    label: 'Open' },
  { value: 'pending', label: 'Pending' },
  { value: 'closed',  label: 'Closed' },
];

const TOPIC_LABELS: Record<string, string> = {
  general: 'General', billing: 'Billing', technical: 'Technical', feature: 'Feature',
};

function ticketTone(status: string): StatusTone {
  if (status === 'open')    return 'info';
  if (status === 'pending') return 'warning';
  return 'muted';
}

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export function SupportInboxPage() {
  const [filter, setFilter] = React.useState<TicketStatus | 'all'>('all');
  const [tickets, setTickets] = React.useState<SupportTicket[] | null>(null);
  const [error,   setError]   = React.useState<string | null>(null);

  function load() {
    setError(null);
    setTickets(null);
    adminSupportApi.list(filter === 'all' ? undefined : filter)
      .then(setTickets)
      .catch((e: unknown) => setError(String((e as any)?.message ?? e)));
  }

  React.useEffect(() => { load(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      <PageHeader
        eyebrow="Support"
        title="Support inbox"
        description="All inbound tickets — public contact form and in-app requests."
      />
      <div className="px-6 sm:px-8 lg:px-10 pb-12 space-y-5">

        {/* Status filter tabs */}
        <div className="flex items-center gap-1.5 flex-wrap">
          {STATUSES.map((s) => (
            <button
              key={s.value}
              type="button"
              onClick={() => setFilter(s.value)}
              className={[
                'h-8 px-3.5 rounded-full text-[12.5px] font-semibold transition-colors',
                filter === s.value
                  ? 'bg-foreground text-background'
                  : 'bg-muted/50 text-muted-foreground hover:text-foreground hover:bg-muted/80',
              ].join(' ')}
            >
              {s.label}
            </button>
          ))}
        </div>

        {error && <ErrorState description={error} onRetry={load} />}
        {tickets === null && !error && <LoadingState variant="page" />}

        {tickets && tickets.length === 0 && (
          <div className={[VISUAL.bento, 'py-14 px-8 text-center max-w-md mx-auto'].join(' ')}>
            <div className="mx-auto mb-5 h-14 w-14 rounded-2xl bg-coral/15 ring-1 ring-coral/25 flex items-center justify-center">
              <LifeBuoy className="h-6 w-6 text-coral" strokeWidth={1.6} />
            </div>
            <p className="text-[18px] font-semibold tracking-[-0.01em] text-foreground">No tickets</p>
            <p className="text-[13.5px] text-muted-foreground mt-1.5 leading-relaxed">
              {filter === 'all' ? 'No support tickets yet.' : `No ${filter} tickets.`}
            </p>
          </div>
        )}

        {tickets && tickets.length > 0 && (
          <section className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
            <ul className="divide-y divide-border/40">
              {tickets.map((t) => (
                <li key={t.id} className="hover:bg-muted/40 transition-colors">
                  <Link
                    to={`/admin/support/${t.id}`}
                    className="flex items-center gap-4 px-5 sm:px-6 py-4"
                  >
                    <div className="h-10 w-10 rounded-2xl bg-muted/50 ring-1 ring-border/40 flex items-center justify-center shrink-0">
                      <LifeBuoy className="h-4 w-4 text-muted-foreground" strokeWidth={1.7} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="text-[14.5px] font-semibold tracking-[-0.005em] text-foreground truncate">
                          {t.subject}
                        </span>
                        <span className="text-[12px] text-muted-foreground">{TOPIC_LABELS[t.topic] ?? t.topic}</span>
                      </div>
                      <p className="text-[12px] text-muted-foreground mt-0.5">
                        {t.contact_name} ({t.contact_email})
                        {t.tenant_id ? ' · In-app' : ' · Public form'}
                        {' · '}{relativeTime(t.last_activity_at)}
                      </p>
                    </div>
                    <span className={statusPill(ticketTone(t.status))}>{t.status}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </div>
  );
}
